import React, { useEffect, useState, useMemo, useCallback } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import DataTable from "react-data-table-component";
import { Typography, MenuItem } from "@material-ui/core";
import axios from "axios";
import swal from "sweetalert";
import { useGlobalSpinnerActionsContext } from "../../context/GlobalSpinnerContext";
import moment from "moment";
import { Configuration } from "../../assets/data/config";
import { useActivityLogger } from "../../hooks/useActivityLogger";
import { apiServices } from "../../services/apiServices";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";

function downloadCSV(filteredData, columns, selectedActivityType) {
  if (!Array.isArray(filteredData) || filteredData.length === 0) return;

  // Step 1: Map data keys (selector) to readable names
  const headers = columns
    .filter((c) => !c.exclude)
    .map((col) => {
      return () => col.name;
    });
  const selectors = columns.map((col) => col.exportSelector);

  // Step 2: Format data for CSV
  const csvRows = [
    headers.map((getHeader, i) => getHeader(filteredData[0] || {})), // dynamic headers based on row values
    ...filteredData.map((row) =>
      selectors.map((selector) => {
        const value = row[selector];
        return value != null ? `"${String(value).replace(/"/g, '""')}"` : "";
      })
    ),
  ];

  // Step 3: Convert to CSV string
  const csvContent = csvRows.map((e) => e.join(",")).join("\n");

  // Step 4: Create download link
  const link = document.createElement("a");
  const now = new Date();
  const formattedDate = now
    .toISOString()
    .replace(/T/, "_")
    .replace(/:/g, "-")
    .replace(/\..+/, "");
  const filename = `${selectedActivityType || "data"}_${formattedDate}.csv`;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.click();

  URL.revokeObjectURL(url);
}

const Export = ({ onExport }) => (
  <Button
    color="primary"
    variant="contained"
    onClick={(e) => onExport(e.target.value)}
  >
    Export
  </Button>
);

const baseStructure = {
  mgmt_key: "",
  mgmt_date: "",
  mgmt_type: "",
  mgmt_other_specify: "",
  mgmt_detail: "",
  mgmt_upload_url: "",
  mgmt_remark: "",
  mgmt_facilityid: "",
  mgmt_ngo: 0,
  mgmt_private: 0,
  mgmt_transport: 0,
  mgmt_dalsa: 0,
  mgmt_traffic: 0,
  mgmt_toll: 0,
  mgmt_truckers: 0,
  mgmt_helpers: 0,
  mgmt_others: 0,
  mgmt_participant: 0,
};

const requiredFields = [
  "mgmt_date",
  "mgmt_participant",
  "mgmt_type",
  "mgmt_other_specify",
  "mgmt_detail",
];

const participantList = {
  mgmt_ngo: "NGO Staff",
  mgmt_private: "Private Clinic/Labs",
  mgmt_transport: "Transport Associates",
  mgmt_dalsa: "DALSA/Legal Service",
  mgmt_traffic: "Traffic Police",
  mgmt_toll: "Toll Plaza",
  mgmt_truckers: "Truckers",
  mgmt_helpers: "Helpers/Allied Population",
  mgmt_others: "Others",
  mgmt_participant: "Total Participants",
};

const ActivityManagement = () => {
  const { sessionUserBoValue } = useActivityLogger();
  const TODAYTIME = moment().format("YYYY-MM-DD");
  const userName = sessionUserBoValue?.displayName || "";

  //   console.log("Configuration", Configuration);
  //   console.log("sessionUserBoValue", sessionUserBoValue);

  const baseURL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:9000/mrunner_api"
      : `${window.location.protocol}//${window.location.host}/mrunner_api`;

  const [formData, setFormData] = useState({
    ...baseStructure,
    mgmt_date: TODAYTIME,
  });
  const [masterList, setMasterList] = useState([]);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [txnType, setTxnType] = useState("create");

  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [selectedStartPeriod, setSelectedStartPeriod] = useState(null);
  const [selectedEndPeriod, setSelectedEndPeriod] = useState(null);

  const setGlobalSpinner = useGlobalSpinnerActionsContext();

  const fetchData = useCallback(async () => {
    const response = await axios.get(`${baseURL}/getActivityMaster`);
    // console.log("response", response);
    setMasterList(
      response.data
        .filter((a) => a.active_flag === "Y")
        .sort((a, b) => {
          if (a.activity_key === 7) return 1;
          if (b.activity_key === 7) return -1;
          return 0; // keep other items in their original order
        })
    );
  }, [baseURL]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const oData = await apiServices.getAPI(
        "organisationUnits.json?fields=id,displayName,parent[id,displayName]&paging=false"
      );
      //   console.log("oData", oData);
      const response = await axios.get(`${baseURL}/getActivitySummary`);
      //   console.log("response", response);
      let sortedArray =
        response?.data?.sort(
          (a, b) => new Date(b.modified_at) - new Date(a.modified_at)
        ) || [];
      sortedArray = sortedArray
        .filter((s) => s.mgmt_status)
        .map((s) => {
          const o =
            oData?.data?.organisationUnits?.find(
              (o) => o.id === s.mgmt_facilityid
            ) || null;
          let fName = s?.mgmt_facilityid || "NA";
          if (o) {
            fName = `${o.displayName} (${o.parent.displayName})`;
          }

          return {
            ...s,
            mgmt_facilityid: fName,
            facilityID: s.mgmt_facilityid,
          };
        });
      setData(sortedArray);
    } catch (error) {
      console.log("Error in fetching the summary details...", error);
    } finally {
    }
  }, [baseURL]);

  const fetchAll = useCallback(async () => {
    setGlobalSpinner(true);

    await Promise.allSettled([fetchData(), fetchSummaryData()]);

    setGlobalSpinner(false);
  }, [fetchData, fetchSummaryData, setGlobalSpinner]);

  useEffect(() => {
    fetchAll();
  }, []);

  const resetValues = useCallback(() => {
    setFormData({
      ...baseStructure,
      mgmt_date: TODAYTIME,
    });
    setErrors({});
    setTxnType("create");
  }, [TODAYTIME]);

  const showForm = useMemo(() => {
    if (!sessionUserBoValue) return false;

    const isAdmin = sessionUserBoValue.userCredentials?.userRoles?.some((r) =>
      Configuration.adminRoles.includes(r.id)
    );

    return !isAdmin; // return true only if user is NOT an admin
  }, [sessionUserBoValue]);

  const isProgramUser = useMemo(() => {
    if (!sessionUserBoValue) return false;

    return sessionUserBoValue.userCredentials?.userRoles?.some((r) =>
      Configuration.noDeleteOptions.includes(r.id)
    );
  }, [sessionUserBoValue]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updatedForm = {
      ...formData,
      [name]: value,
      mgmt_facilityid: sessionUserBoValue?.organisationUnits?.[0]?.id || null,
    };

    if (Object.keys(participantList).includes(name)) {
      updatedForm.mgmt_participant =
        Number(updatedForm.mgmt_ngo) +
        Number(updatedForm.mgmt_private) +
        Number(updatedForm.mgmt_transport) +
        Number(updatedForm.mgmt_dalsa) +
        Number(updatedForm.mgmt_traffic) +
        Number(updatedForm.mgmt_toll) +
        Number(updatedForm.mgmt_truckers) +
        Number(updatedForm.mgmt_helpers) +
        Number(updatedForm.mgmt_others);
    }

    if (name === "mgmt_type") {
      updatedForm.mgmt_other_specify = "";
    }
    setFormData(updatedForm);
  };

  const byFacilityData = useMemo(() => {
    let fData = [];
    if (sessionUserBoValue) {
      fData = [...data];
      if (showForm) {
        fData = fData.filter(
          (d) => d.facilityID === sessionUserBoValue?.organisationUnits?.[0]?.id
        );
      }
    }

    fData = fData.map((f) => {
      //   console.log("masterList", masterList);
      const isFound = masterList.find(
        (m) => Number(m.activity_key) === Number(f.mgmt_type)
      );
      //   console.log("isFound", isFound);
      return {
        ...f,
        mgmt_type: isFound?.activity_name || f.mgmt_type,
      };
    });
    return fData;
  }, [sessionUserBoValue, data, showForm, masterList]);

  const filteredData = useMemo(() => {
    let uData = [...byFacilityData];

    if (selectedActivityType) {
      uData =
        selectedActivityType === "All"
          ? uData
          : uData.filter((d) => d.mgmt_type === selectedActivityType);
    }

    if (selectedStartPeriod && selectedEndPeriod) {
      uData = uData.filter((d) =>
        moment(d.mgmt_date).isBetween(
          selectedStartPeriod,
          selectedEndPeriod,
          "day",
          "[]"
        )
      );
    }

    return uData.filter(
      (d) =>
        d.mgmt_date.toLowerCase().includes(searchName.toLowerCase()) ||
        d.mgmt_type.toLowerCase().includes(searchName.toLowerCase()) ||
        d.mgmt_detail.toLowerCase().includes(searchName.toLowerCase()) ||
        d.mgmt_facilityid.toLowerCase().includes(searchName.toLowerCase()) ||
        d.mgmt_other_specify.toLowerCase().includes(searchName.toLowerCase()) ||
        d.mgmt_remark.toLowerCase().includes(searchName.toLowerCase())
    );
  }, [
    searchName,
    byFacilityData,
    selectedEndPeriod,
    selectedStartPeriod,
    selectedActivityType,
  ]);

  const onEdit = useCallback(
    (row) => {
      setTxnType("Edit");
      const isFound = masterList.find((m) => m.activity_name === row.mgmt_type);
      setFormData({
        ...row,
        mgmt_type: isFound?.activity_key || row.mgmt_type,
      });
    },
    [masterList]
  );

  const applyCommonCellStyle = ({
    name,
    selector,
    tooltip = true,
    width = 150,
    cellFormatType = "default",
  }) => ({
    name,
    selector: (row) => row[selector],
    exportSelector: selector,
    width: `${width}px`, // fixed width for header and cell
    cell: (row) =>
      cellFormatType === "formatLink" ? (
        <a
          href={row[selector]}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            wordBreak: "break-all",
            color: "blue",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={row[selector]} // tooltip for full text on hover
        >
          {row[selector]}
        </a>
      ) : (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={tooltip ? row[selector] : undefined}
        >
          {cellFormatType === "default" ? row[selector] : null}
        </div>
      ),
    wrap: true,
    sortable: true,
  });

  const updateData = useCallback(
    async (data) => {
      setGlobalSpinner(true);
      try {
        await axios.post(`${baseURL}/updateActivity`, data, {
          headers: { "Content-Type": "application/json" },
        });
        swal({
          title: "Success",
          text:
            data.txnType === "delete"
              ? "Data deleted successfully..."
              : "Data saved successfully...",
          icon: "success",
          button: "Close",
        }).then((value) => {
          if (value) {
            fetchAll();
            axios.post(
              process.env.NODE_ENV === "development"
                ? `http://localhost:9000/mrunner_api/refreshDashboards`
                : `${window.location.protocol}//${window.location.host}/mrunner_api/refreshDashboards`,
              { r_id: 4 }
            );
          }
        });
      } catch (error) {
        console.log("Error while saving the data...", error);
        swal({
          title: "Error",
          text: "Error while saving the data...",
          icon: "error",
          button: "Close",
        });
      } finally {
        resetValues();
        setGlobalSpinner(false);
      }
    },
    [baseURL, fetchAll, resetValues, setGlobalSpinner]
  );

  const onDelete = useCallback(
    (row) => {
      swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this record!",
        icon: "warning",
        buttons: ["Cancel", "Delete"],
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) {
          const data = {
            mgmt_key: row.mgmt_key,
            mgmt_created_by: userName,
            txnType: "delete",
          };
          // console.log("data", data);
          updateData(data);
        } else {
          console.log("Your record is safe!");
        }
      });
    },
    [userName, updateData]
  );

  const columns = useMemo(() => {
    const cols = [
      applyCommonCellStyle({
        name: "Activity Date",
        selector: "mgmt_date",
      }),
      applyCommonCellStyle({
        name: "Activity Type",
        selector: "mgmt_type",
      }),
      applyCommonCellStyle({
        name: "Activity Type (Other)",
        selector: "mgmt_other_specify",
      }),
    ];

    Object.keys(participantList).forEach((p) => {
      cols.push(
        applyCommonCellStyle({
          name: participantList[p],
          selector: p,
        })
      );
    });

    cols.push(
      applyCommonCellStyle({
        name: "Activity Details",
        selector: "mgmt_detail",
      }),
      applyCommonCellStyle({
        name: "Activity Remarks",
        selector: "mgmt_remark",
      }),
      applyCommonCellStyle({
        name: "Attachment",
        selector: "mgmt_upload_url",
        cellFormatType: "formatLink",
        width: 250,
      }),
      applyCommonCellStyle({
        name: "User",
        selector: "mgmt_created_by",
      }),
      applyCommonCellStyle({
        name: "Created Date",
        selector: "created_at",
        width: 250,
      }),
      applyCommonCellStyle({
        name: "Updated Date",
        selector: "modified_at",
        width: 250,
      })
    );

    if (!isProgramUser) {
      cols.push({
        name: "Action",
        selector: null,
        sortable: true,
        exclude: true,
        wrap: true,
        width: "150px",
        cell: (row) => (
          <>
            {showForm ? (
              <Button
                variant="contained"
                color="primary"
                component="span"
                startIcon={<CloudUploadIcon />}
                onClick={() => onEdit(row)}
              >
                Edit
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                component="span"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(row)}
              >
                Delete
              </Button>
            )}
          </>
        ),
      });
    }
    return cols;
  }, [showForm, onEdit, onDelete, isProgramUser]);

  const actionsMemo = React.useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "end",
        }}
      >
        <div
          style={{
            width: "200px",
            marginBottom: "10px",
            fontSize: "14px",
            paddingLeft: "20px",
          }}
        >
          <label>Activity Type</label>
          <TextField
            select
            name="txnType"
            value={selectedActivityType || "All"}
            onChange={(e) => setSelectedActivityType(e.target.value)}
            fullWidth
            variant="outlined"
            className="p-0"
            placeholder="Activity Type"
          >
            <MenuItem value="" disabled>
              Select Activity Type
            </MenuItem>
            <MenuItem value="All">All</MenuItem>
            {masterList.length > 0 ? (
              masterList.map((item, i) => (
                <MenuItem value={item.activity_name} key={"txnType" + i}>
                  {item.activity_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={null} disabled>
                No Data to Display
              </MenuItem>
            )}
          </TextField>
        </div>
        <div
          style={{
            width: "200px",
            marginBottom: "10px",
            fontSize: "14px",
            paddingLeft: "20px",
          }}
        >
          <label>Start Date</label>
          <TextField
            type="date"
            name="startDate"
            value={selectedStartPeriod}
            onChange={(e) => setSelectedStartPeriod(e.target.value)}
            fullWidth
            variant="outlined"
            className="p-0"
            InputLabelProps={{ shrink: true }}
          />
        </div>
        <div
          style={{
            width: "200px",
            marginBottom: "10px",
            fontSize: "14px",
            paddingLeft: "20px",
          }}
        >
          <label>End Date</label>
          <TextField
            type="date"
            name="endDate"
            value={selectedEndPeriod}
            onChange={(e) => setSelectedEndPeriod(e.target.value)}
            fullWidth
            variant="outlined"
            className="p-0"
            InputLabelProps={{ shrink: true }}
          />
        </div>
        <div
          style={{
            width: "200px",
            marginBottom: "10px",
            fontSize: "14px",
            paddingLeft: "20px",
          }}
        >
          <label>Search</label>
          <input
            style={{
              width: "90%",
              padding: "10px",
              borderRadius: "4px",
              border: "1px solid rgba(0, 0, 0, 0.26)",
            }}
            placeholder="Search..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: "10px", paddingLeft: "20px" }}>
          <Export
            onExport={() =>
              downloadCSV(filteredData, columns, selectedActivityType)
            }
          />
        </div>
      </div>
    ),
    [
      filteredData,
      columns,
      searchName,
      selectedActivityType,
      masterList,
      selectedEndPeriod,
      selectedStartPeriod,
    ]
  );

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;
    // console.log("formData", formData);

    const rFields =
      formData.mgmt_type === 7
        ? requiredFields
        : requiredFields.filter((r) => !["mgmt_other_specify"].includes(r));
    rFields.forEach((field) => {
      if (!formData[field]) {
        formErrors[field] = "This field is required";
        isValid = false;
      }
    });
    setErrors(formErrors);
    // console.log("formErrors", formErrors);
    // console.log("isValid", isValid);
    return isValid;
  };

  const handleSubmit = async (e) => {
    // console.log("e", e);
    e.preventDefault();
    if (!validateForm()) return;

    // console.log("formData", formData);
    const data = {
      mgmt_key: formData.mgmt_key || null,
      mgmt_date: formData.mgmt_date,
      mgmt_ngo: formData.mgmt_ngo,
      mgmt_private: formData.mgmt_private,
      mgmt_transport: formData.mgmt_transport,
      mgmt_dalsa: formData.mgmt_dalsa,
      mgmt_traffic: formData.mgmt_traffic,
      mgmt_toll: formData.mgmt_toll,
      mgmt_truckers: formData.mgmt_truckers,
      mgmt_helpers: formData.mgmt_helpers,
      mgmt_others: formData.mgmt_others,
      mgmt_participant: formData.mgmt_participant,
      mgmt_type: formData.mgmt_type,
      mgmt_other_specify: formData.mgmt_other_specify,
      mgmt_detail: formData.mgmt_detail,
      mgmt_upload_url: formData.mgmt_upload_url,
      mgmt_remark: formData.mgmt_remark,
      mgmt_facilityid:
        formData?.facilityID ||
        sessionUserBoValue?.organisationUnits?.[0]?.id ||
        null,
      mgmt_created_by: userName,
      txnType,
    };
    // console.log("data", data);
    updateData(data);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const maxSizeMB = 5;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      if (file.size > maxSizeBytes) {
        swal({
          title: "File too large",
          text: `The selected file exceeds ${maxSizeMB}MB. Please upload a smaller file.`,
          icon: "warning",
          button: "OK",
        });
        return;
      }
      const formData = new FormData();
      formData.append("file", file); // 'file' must match backend expected field name
      setGlobalSpinner(true);
      try {
        const response = await apiServices.postMultiPartFormDataAPI(
          `dashboardIndicator/uploadImage`,
          formData
        );
        // console.log("response file upload", response);
        setFormData((prev) => ({
          ...prev,
          mgmt_upload_url: response.data,
        }));
      } catch (error) {
        swal({
          title: "Fail",
          text: "Fail to upload",
          icon: "error",
          button: "Close",
        });
      } finally {
        setGlobalSpinner(false);
      }
    }
  };

  return (
    <div
      style={{
        padding: "65px 0px 0px",
        height: "calc(100vh - 64px)",
        width: "100%",
        position: "relative",
        overflowY: "auto",
      }}
    >
      {showForm && (
        <div>
          <form onSubmit={handleSubmit} className="inventoryForm">
            <Grid container spacing={3} className="inventorySection">
              <Grid item xs={12} sm={3}>
                <Typography>
                  Activity Date<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  type="date"
                  name="mgmt_date"
                  value={formData.mgmt_date}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.mgmt_date}
                  helperText={errors.mgmt_date}
                  inputProps={{
                    max: TODAYTIME,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  Activity Type<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  select
                  name="mgmt_type"
                  value={formData.mgmt_type}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.mgmt_type}
                  helperText={errors.mgmt_type}
                >
                  {masterList.length > 0 ? (
                    masterList.map((item, i) => (
                      <MenuItem value={item.activity_key} key={"mgmt_type" + i}>
                        {item.activity_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={null} disabled>
                      No Data to Display
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              {formData.mgmt_type === 7 && (
                <Grid item xs={12} sm={3}>
                  <Typography>
                    Activity Type (Other)<sup style={{ color: "red" }}>*</sup>
                  </Typography>
                  <TextField
                    name="mgmt_other_specify"
                    value={formData.mgmt_other_specify}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.mgmt_other_specify}
                    helperText={errors.mgmt_other_specify}
                  />
                </Grid>
              )}
              {Object.keys(participantList).map((p) => (
                <Grid item xs={12} sm={3}>
                  <Typography>
                    {participantList[p]}
                    <sup style={{ color: "red" }}>*</sup>
                  </Typography>
                  <TextField
                    type="number"
                    inputProps={{ min: 0 }}
                    name={p}
                    value={formData[p]}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors[p]}
                    helperText={errors[p]}
                    disabled={p === "mgmt_participant"}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={3}>
                <Typography>
                  Activity Details<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  name="mgmt_detail"
                  multiline
                  minRows={1}
                  maxRows={6}
                  value={formData.mgmt_detail}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.mgmt_detail}
                  helperText={errors.mgmt_detail}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography>Activity Remarks</Typography>
                <TextField
                  name="mgmt_remark"
                  multiline
                  minRows={1}
                  maxRows={6}
                  value={formData.mgmt_remark}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.mgmt_remark}
                  helperText={errors.mgmt_remark}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  Attachment <sub style={{ opacity: 0.7 }}>(Up to 5 MB)</sub>
                </Typography>
                <input
                  accept="*"
                  style={{ display: "none" }}
                  id="contained-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="contained-button-file">
                  <Button
                    variant="contained"
                    color="primary"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload File
                  </Button>
                </label>
                {formData.mgmt_upload_url && (
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    Uploaded File:{" "}
                    <a
                      href={formData.mgmt_upload_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: "break-all", color: "blue" }}
                    >
                      {formData.mgmt_upload_url}
                    </a>
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={3} style={{ marginTop: "20px" }}>
              <Grid item xs={8}></Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={resetValues}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>
      )}
      <div className="inventoryTable" style={{ marginTop: "30px" }}>
        <DataTable
          title="Activity Report"
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          striped
          persistTableHead
          fixedHeader
          actions={actionsMemo}
          customStyles={{
            rows: {
              style: {
                maxHeight: "48px",
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default ActivityManagement;
